// ./src/hooks/useTicketTypes.ts
//
// Paginated loader for an event's ticket types (active + disabled, so the
// management page can re-enable disabled ones), by display order. Server-side
// search, a running `total` from the list result, and load-more append.
//
// `loadAll` exists for drag-to-reorder: a position is only meaningful against the
// COMPLETE list — dropping a row while later pages are unloaded would write an
// orderNo relative to rows that aren't there — so the page pulls everything before
// entering reorder mode.

import { useCallback, useEffect, useRef, useState } from "react";
import { OpResult, OP_RESULT_CODES } from "@sdflc/api-helpers";
import { STATUSES } from "@sdflc/utils";

import { TicketTypeService, TicketType, TicketTypeFilter } from "@/utils/api";
import { DEFAULT_PAGE_SIZE } from "@/config";
import { useRefreshOnWriteTimeout } from "./useRefreshOnWriteTimeout";

// Runaway-loop backstop for the reorder pull, not an expected limit.
const LOAD_ALL_MAX_PAGES = 20;

export interface UseTicketTypesReturn {
  tickets: TicketType[];
  /** Ticket types matching the filter across ALL pages. */
  total: number;
  loading: boolean;
  loadingMore: boolean;
  /** True once the first fetch has completed (list may still be empty). */
  isLoaded: boolean;
  hasMore: boolean;
  error: OpResult | null;
  reload: () => Promise<void>;
  loadMore: () => Promise<void>;
  /** Pulls every remaining page (reorder needs the whole list). */
  loadAll: () => Promise<void>;
}

export const useTicketTypes = (
  eventId?: string | null,
  searchKeyword?: string,
  pageSize: number = DEFAULT_PAGE_SIZE
): UseTicketTypesReturn => {
  const [tickets, setTickets] = useState<TicketType[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<OpResult | null>(null);
  const [page, setPage] = useState(1);

  const callIdRef = useRef(0);
  const search = (searchKeyword ?? "").trim();
  const hasMore = tickets.length < total;

  const buildFilter = useCallback((): TicketTypeFilter => {
    const filter: TicketTypeFilter = {
      eventId: [eventId as string],
      status: [STATUSES.ACTIVE, STATUSES.DISABLED],
    };

    if (search) {
      filter.searchKeyword = search;
    }

    return filter;
  }, [eventId, search]);

  const load = useCallback(
    async (targetPage: number, append: boolean) => {
      if (!eventId) {
        setTickets([]);
        setTotal(0);
        setIsLoaded(true);

        return;
      }

      const myCall = ++callIdRef.current;

      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        // A reload owns the list: a Load more it supersedes is dropped, and so is its spinner (#114).
        setLoadingMore(false);
      }

      setError(null);

      const result = await TicketTypeService.list(buildFilter(), { page: targetPage, pageSize });

      if (myCall !== callIdRef.current) {
        return;
      }

      if (append) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }

      setIsLoaded(true);

      if (result.code === OP_RESULT_CODES.OK && Array.isArray(result.data)) {
        const rows = result.data as TicketType[];
        setTickets((prev) => (append ? [...prev, ...rows] : rows));
        setTotal(typeof result.total === "number" ? result.total : rows.length);
        setPage(targetPage);
      } else {
        setError(result);

        if (!append) {
          setTickets([]);
        }
      }
    },
    [eventId, buildFilter, pageSize]
  );

  const reload = useCallback(async () => {
    await load(1, false);
  }, [load]);

  // ROADMAP #131 — a save that timed out may still have landed: re-read what is shown.
  useRefreshOnWriteTimeout(reload, isLoaded);

  const loadMore = useCallback(async () => {
    if (loading || loadingMore || !hasMore) {
      return;
    }

    await load(page + 1, true);
  }, [load, loading, loadingMore, hasMore, page]);

  /**
   * Fetches every matching page and replaces the list in one commit — rather than
   * appending page by page — so a half-loaded list can never be dragged against.
   */
  const loadAll = useCallback(async () => {
    if (!eventId) {
      return;
    }

    const myCall = ++callIdRef.current;
    setLoading(true);
    // A reload owns the list: a Load more it supersedes is dropped, and so is its spinner (#114).
    setLoadingMore(false);
    setError(null);

    const all: TicketType[] = [];
    let matched = 0;
    let lastPage = 1;

    for (let targetPage = 1; targetPage <= LOAD_ALL_MAX_PAGES; targetPage++) {
      const result = await TicketTypeService.list(buildFilter(), { page: targetPage, pageSize });

      if (myCall !== callIdRef.current) {
        return;
      }

      if (result.code !== OP_RESULT_CODES.OK || !Array.isArray(result.data)) {
        setLoading(false);
        setError(result);

        return;
      }

      const rows = result.data as TicketType[];
      all.push(...rows);
      matched = typeof result.total === "number" ? result.total : all.length;
      lastPage = targetPage;

      if (rows.length < pageSize || all.length >= matched) {
        break;
      }
    }

    setLoading(false);
    setIsLoaded(true);
    setTickets(all);
    setTotal(matched);
    setPage(lastPage);
  }, [eventId, buildFilter, pageSize]);

  // Refetch page 1 whenever the event or the (server-side) search changes.
  useEffect(() => {
    load(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, search, pageSize]);

  return { tickets, total, loading, loadingMore, isLoaded, hasMore, error, reload, loadMore, loadAll };
};
